import { useState } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ADICIONAIS_BOOLEAN, ADICIONAIS_QUANTIDADE } from '../../data/adicionais'
import { User, MapPin, Calendar, Package, Truck, Tag } from 'lucide-react'

import { formatCurrency, descricaoMovel, formatTelefone } from '../../utils/fmt'

function formatarPreco(raw) {
  const nums = String(raw).replace(/\D/g, '')
  if (!nums) return ''
  const n = Number(nums) / 100
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function parsearPreco(v) {
  return Number(String(v).replace(/\./g, '').replace(',', '.')) || 0
}

function formatDate(d) {
  if (!d) return '—'
  try { return format(new Date(d + 'T12:00:00'), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }) } catch { return d }
}

const FORMAS_LABEL = {
  pix: 'Pix', dinheiro: 'Dinheiro', credito: 'Cartão de crédito', debito: 'Cartão de débito',
}
const PRAZOS_LABEL = {
  ato: 'Tudo no ato', '50_50': '50% pedido + 50% na entrega',
}

function adicionaisAtivos(adicionais) {
  if (!adicionais) return []
  const r = []
  if (adicionais.puxadorTipo) {
    if (adicionais.puxadorTipo === 'Sem puxador') {
      r.push('Sem puxador')
    } else {
      const cor = adicionais.puxadorCor ? ` ${adicionais.puxadorCor}` : ''
      const qtd = adicionais.puxadorQtd > 0 ? ` (${adicionais.puxadorQtd}x)` : ''
      r.push(`Puxador ${adicionais.puxadorTipo}${cor}${qtd}`)
    }
  }
  ADICIONAIS_BOOLEAN.forEach(({ key, label }) => { if (adicionais[key]) r.push(label) })
  ADICIONAIS_QUANTIDADE.forEach(({ key, label }) => { if (adicionais[key] > 0) r.push(`${label}: ${adicionais[key]}`) })
  return r
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="card p-5">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg bg-[#FFF9C2] flex items-center justify-center">
          <Icon size={14} className="text-[#E6A000]" />
        </div>
        <h4 className="text-sm font-semibold text-slate-700">{title}</h4>
      </div>
      {children}
    </div>
  )
}

function CardMovelLogistica({ movel, index }) {
  const ads = adicionaisAtivos(movel.adicionais)
  const isDuploComprimento = movel.comprimento2 && Number(movel.comprimento2) > 0

  return (
    <div className="border-2 border-slate-200 rounded-xl p-4 space-y-3">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#FFF9C2] text-[#CC8800] text-sm font-bold flex items-center justify-center shrink-0">
            {index + 1}
          </div>
          <p className="font-bold text-slate-800 text-base">{movel.modelo}</p>
        </div>
        <span className="text-base font-bold text-[#CC8800]">{formatCurrency(movel.preco)}</span>
      </div>

      {/* Cor */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400 mb-0.5">Cor</p>
          <p className="font-semibold text-slate-800">{movel.cor || '—'}</p>
        </div>

        {/* Medidas */}
        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400 mb-0.5">Medidas (cm)</p>
          <p className="font-semibold text-slate-800">
            {isDuploComprimento
              ? `${movel.comprimento}+${movel.comprimento2} × ${movel.profundidade} × ${movel.altura}`
              : [movel.comprimento, movel.profundidade, movel.altura].filter(Boolean).join(' × ') || '—'
            }
          </p>
        </div>

        {isDuploComprimento && (
          <>
            <div className="bg-amber-50 rounded-lg px-3 py-2">
              <p className="text-xs text-amber-600 mb-0.5">Comprimento lado A</p>
              <p className="font-bold text-amber-800">{movel.comprimento} cm</p>
            </div>
            <div className="bg-amber-50 rounded-lg px-3 py-2">
              <p className="text-xs text-amber-600 mb-0.5">Comprimento lado B</p>
              <p className="font-bold text-amber-800">{movel.comprimento2} cm</p>
            </div>
          </>
        )}

        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400 mb-0.5">Altura</p>
          <p className="font-semibold text-slate-800">{movel.altura ? `${movel.altura} cm` : '—'}</p>
        </div>

        <div className="bg-slate-50 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400 mb-0.5">Profundidade</p>
          <p className="font-semibold text-slate-800">{movel.profundidade ? `${movel.profundidade} cm` : '—'}</p>
        </div>
      </div>

      {/* Adicionais */}
      {ads.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 mb-1.5">Adicionais</p>
          <div className="flex flex-wrap gap-1.5">
            {ads.map(a => (
              <span key={a} className="bg-[#111111] text-white text-xs px-2.5 py-1 rounded-full font-medium">
                {a}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Observações */}
      {movel.observacoes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
          <p className="text-xs text-yellow-700 font-semibold mb-0.5">Observação</p>
          <p className="text-sm text-yellow-800">"{movel.observacoes}"</p>
        </div>
      )}
    </div>
  )
}

export function StepResumo() {
  const [aba, setAba]         = useState('resumo')
  const { watch, control }    = useFormContext()
  const cliente               = watch('cliente') || {}
  const moveis                = watch('moveis') || []
  const prazo                 = watch('prazo')
  const vendedora             = watch('vendedora')
  const formaPagamento        = watch('formaPagamento')
  const prazoPagamento        = watch('prazoPagamento')
  const prazoPagCustom        = watch('prazoPagamentoCustom')
  const total                 = moveis.reduce((s, m) => s + Number(m.preco || 0), 0)

  const prazoLabel            = prazoPagamento === 'custom'
    ? prazoPagCustom || 'Personalizado'
    : PRAZOS_LABEL[prazoPagamento] || ''
  const freteRaw              = watch('frete') || ''
  const freteNum              = freteRaw ? Number(String(freteRaw).replace(/\./g,'').replace(',','.')) : 0
  const freteLocal            = watch('freteLocal') || ''
  const valorPendenteRaw      = watch('valorPendenteEntrega') || ''
  const valorPendenteNum      = valorPendenteRaw ? Number(String(valorPendenteRaw).replace(/\./g,'').replace(',','.')) : 0
  const precoFinalRaw         = watch('precoFinal') || ''
  const precoFinalNum         = parsearPreco(precoFinalRaw)
  const temDesconto           = precoFinalNum > 0 && precoFinalNum < total
  const descontoValor         = temDesconto ? total - precoFinalNum : 0
  const descontoPct           = temDesconto ? (descontoValor / total) * 100 : 0

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Revise o pedido antes de salvar</h3>

      {/* Abas */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {[
          { id: 'resumo',    label: 'Resumo'    },
          { id: 'logistica', label: 'Logística' },
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setAba(tab.id)}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all
              ${aba === tab.id
                ? 'bg-white text-[#111111] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── ABA RESUMO ───────────────────────────────────────────────────── */}
      {aba === 'resumo' && (
        <>
          <Section icon={User} title="Dados do cliente">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><dt className="text-slate-400 text-xs">Nome</dt><dd className="font-medium">{cliente.nome || '—'}</dd></div>
              <div><dt className="text-slate-400 text-xs">Documento</dt><dd className="font-medium">{cliente.documento || '—'} ({cliente.tipoDocumento})</dd></div>
              <div><dt className="text-slate-400 text-xs">Contato</dt><dd className="font-medium">{formatTelefone(cliente.contato) || '—'}</dd></div>
              <div><dt className="text-slate-400 text-xs">E-mail</dt><dd className="font-medium">{cliente.email || '—'}</dd></div>
              {cliente.tipoDocumento === 'CNPJ' && (
                <div className="col-span-2"><dt className="text-slate-400 text-xs">ICMS</dt><dd className="font-medium capitalize">{cliente.contribuinteICMS === 'sim' ? 'Contribuinte ICMS' : 'Não contribuinte'}</dd></div>
              )}
            </dl>
          </Section>

          <Section icon={MapPin} title="Entrega">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <div className="col-span-2">
                <dt className="text-slate-400 text-xs">Rua / Avenida</dt>
                <dd className="font-medium">{[cliente.rua, cliente.numero].filter(Boolean).join(', ') || '—'}</dd>
              </div>
              {cliente.complemento && (
                <div className="col-span-2">
                  <dt className="text-slate-400 text-xs">Complemento</dt>
                  <dd className="font-medium">{cliente.complemento}</dd>
                </div>
              )}
              <div>
                <dt className="text-slate-400 text-xs">Bairro</dt>
                <dd className="font-medium">{cliente.bairro || '—'}</dd>
              </div>
              <div>
                <dt className="text-slate-400 text-xs">Cidade</dt>
                <dd className="font-medium">{cliente.cidade || '—'}</dd>
              </div>
            </dl>
          </Section>

          <Section icon={Calendar} title="Prazo e pagamento">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><dt className="text-slate-400 text-xs">Prazo de entrega</dt><dd className="font-medium">{formatDate(prazo)}</dd></div>
              {vendedora && <div><dt className="text-slate-400 text-xs">Vendedora</dt><dd className="font-medium">{vendedora}</dd></div>}
              {watch('pagamentoDividido') ? (
                <div className="col-span-2">
                  <dt className="text-slate-400 text-xs">Formas de pagamento</dt>
                  <dd className="font-medium space-y-0.5 mt-0.5">
                    {watch('entradaValor') && (
                      <div className="text-sm">
                        <span className="text-slate-500">Entrada:</span>{' '}
                        {formatCurrency(Number(String(watch('entradaValor')).replace(/\./g,'').replace(',','.')) || 0)}
                        {watch('entradaForma') && <span className="text-slate-500"> · {FORMAS_LABEL[watch('entradaForma')] || watch('entradaForma')}</span>}
                        {watch('entradaObs') && <span className="text-slate-400 italic"> · {watch('entradaObs')}</span>}
                      </div>
                    )}
                    {watch('saldoValor') && (
                      <div className="text-sm">
                        <span className="text-slate-500">Saldo:</span>{' '}
                        {formatCurrency(Number(String(watch('saldoValor')).replace(/\./g,'').replace(',','.')) || 0)}
                        {watch('saldoForma') && <span className="text-slate-500"> · {FORMAS_LABEL[watch('saldoForma')] || watch('saldoForma')}</span>}
                        {watch('saldoObs') && <span className="text-slate-400 italic"> · {watch('saldoObs')}</span>}
                      </div>
                    )}
                  </dd>
                </div>
              ) : (
                formaPagamento && <div><dt className="text-slate-400 text-xs">Forma de pagamento</dt><dd className="font-medium">{FORMAS_LABEL[formaPagamento] || formaPagamento}</dd></div>
              )}
              <div>
                <dt className="text-slate-400 text-xs">Frete</dt>
                <dd className="font-medium">
                  {freteNum > 0 ? formatCurrency(freteNum) : 'Grátis'}
                  {freteLocal?.trim() && <span className="text-slate-400 font-normal ml-1">· {freteLocal.trim()}</span>}
                </dd>
              </div>
              {valorPendenteNum > 0 && <div><dt className="text-slate-400 text-xs">Pendente na entrega</dt><dd className="font-semibold text-orange-600">{formatCurrency(valorPendenteNum)}</dd></div>}
              {prazoLabel && <div className="col-span-2"><dt className="text-slate-400 text-xs">Condição</dt><dd className="font-medium">{prazoLabel}</dd></div>}
            </dl>
          </Section>

          <Section icon={Package} title={`Móveis (${moveis.length})`}>
            <div className="space-y-3">
              {moveis.map((m, i) => (
                <div key={m.id} className="border border-slate-100 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-[#FFF9C2] text-[#CC8800] text-xs font-bold flex items-center justify-center shrink-0">
                        {i + 1}
                      </span>
                      <p className="text-sm text-slate-700 leading-relaxed">{descricaoMovel(m)}</p>
                    </div>
                    <span className="text-sm font-bold text-[#CC8800] shrink-0">{formatCurrency(m.preco)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-600">Total do pedido</span>
              <span className="text-lg font-bold text-[#CC8800]">{formatCurrency(total)}</span>
            </div>
          </Section>

          {/* Desconto */}
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 rounded-lg bg-[#FFF9C2] flex items-center justify-center">
                <Tag size={14} className="text-[#E6A000]" />
              </div>
              <h4 className="text-sm font-semibold text-slate-700">Desconto</h4>
            </div>

            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-slate-500">Total dos itens (sem desconto)</span>
              <span className="font-semibold text-slate-700">{formatCurrency(total)}</span>
            </div>

            <div>
              <label className="label">Preço final do pedido <span className="text-slate-400 font-normal">(opcional)</span></label>
              <Controller
                name="precoFinal"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="text"
                    inputMode="numeric"
                    className="input mt-1"
                    placeholder={formatCurrency(total)}
                    onChange={e => field.onChange(formatarPreco(e.target.value))}
                  />
                )}
              />
              <p className="text-xs text-slate-400 mt-1">
                Se houver desconto, informe o valor final cobrado. O desconto será rateado entre os itens.
              </p>
            </div>

            {temDesconto && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Desconto</span>
                  <span className="font-semibold text-green-700">
                    − {formatCurrency(descontoValor)} ({descontoPct.toFixed(1)}%)
                  </span>
                </div>
                <div className="border-t border-green-200 pt-2 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700">Total final</span>
                  <span className="text-lg font-bold text-[#CC8800]">{formatCurrency(precoFinalNum)}</span>
                </div>
                <div className="border-t border-green-100 pt-2">
                  <p className="text-xs text-slate-400 mb-1.5">Rateio por item:</p>
                  <div className="space-y-1">
                    {moveis.map((m, i) => {
                      const precoItem = m.preco * (precoFinalNum / total)
                      return (
                        <div key={m.id} className="flex items-center justify-between text-xs text-slate-600">
                          <span className="truncate max-w-[60%]">{i + 1}. {m.modelo}</span>
                          <span>
                            <span className="line-through text-slate-400 mr-1">{formatCurrency(m.preco)}</span>
                            <span className="font-semibold text-green-700">{formatCurrency(precoItem)}</span>
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Valor Total + Valor Final (com frete) ───────────────────── */}
          {/* Valor Total = soma dos móveis (com desconto se aplicado).
              Valor Final = Valor Total + Frete. */}
          <div className="card p-5 bg-gradient-to-br from-[#FFF9C2]/40 to-white border-2 border-[#F5C200]/30">
            <h4 className="text-sm font-semibold text-slate-700 mb-4">Resumo financeiro</h4>

            <div className="space-y-2.5">
              {/* Valor Total — subtotal dos móveis (com desconto se houver) */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Valor total</p>
                  <p className="text-[10px] text-slate-400">
                    {temDesconto ? 'soma dos móveis com desconto' : 'soma dos móveis'}
                  </p>
                </div>
                <span className="text-base font-bold text-slate-800">{formatCurrency(temDesconto ? precoFinalNum : total)}</span>
              </div>

              {/* Frete */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Frete</p>
                  {freteLocal?.trim() && (
                    <p className="text-[10px] text-slate-400">{freteLocal.trim()}</p>
                  )}
                </div>
                <span className="text-sm font-semibold text-slate-700">
                  {freteNum > 0 ? `+ ${formatCurrency(freteNum)}` : 'Grátis'}
                </span>
              </div>

              {/* Valor Final */}
              <div className="flex items-center justify-between pt-3 border-t-2 border-[#F5C200]/40">
                <div>
                  <p className="text-base font-bold text-slate-800">Valor final</p>
                  <p className="text-[10px] text-slate-500">total + frete</p>
                </div>
                <span className="text-2xl font-black text-[#CC8800]">
                  {formatCurrency((temDesconto ? precoFinalNum : total) + freteNum)}
                </span>
              </div>
            </div>
          </div>

          {/* Observações gerais */}
          <div className="card p-5">
            <label className="label">Observações gerais do pedido</label>
            <Controller
              name="observacoesGerais"
              control={control}
              render={({ field }) => (
                <textarea
                  {...field}
                  rows={3}
                  className="input resize-none mt-1"
                  placeholder="Informações adicionais para produção, combinações, etc..."
                />
              )}
            />
          </div>
        </>
      )}

      {/* ── ABA LOGÍSTICA ────────────────────────────────────────────────── */}
      {aba === 'logistica' && (
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Truck size={15} className="text-slate-400" />
              <span>Prazo de entrega: <strong className="text-slate-800">{formatDate(prazo)}</strong></span>
            </div>
            <span className="font-bold text-[#CC8800]">{moveis.length} móvel{moveis.length !== 1 ? 'is' : ''}</span>
          </div>

          {moveis.map((m, i) => (
            <CardMovelLogistica key={m.id} movel={m} index={i} />
          ))}

          <div className="bg-[#FFF9C2] border border-[#F5C200]/40 rounded-xl px-4 py-3">
            {temDesconto ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-700">Total final</p>
                  <p className="text-xs text-slate-400">
                    Desconto de {formatCurrency(descontoValor)} ({descontoPct.toFixed(1)}%) aplicado
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-[#CC8800]">{formatCurrency(precoFinalNum)}</p>
                  <p className="text-xs line-through text-slate-400">{formatCurrency(total)}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">Total do pedido</span>
                <span className="text-xl font-bold text-[#CC8800]">{formatCurrency(total)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
